import { useEffect, useState } from 'react';
import { rates, stripeConnect, ApiClientError } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Spinner } from '../components/ui';

/**
 * Stripe Connect onboarding for the USD card onramp rail. The integrator
 * connects their own Stripe account; card charges route there. USD onramps are
 * refused until this is active.
 */
function StripeConnectCard() {
  const status = useResource(() => stripeConnect.status(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Coming back from Stripe onboarding? Re-pull the live status.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('stripe');
    if (p === 'done' || p === 'refresh') {
      stripeConnect.refresh().then(() => status.reload()).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect() {
    setError(null);
    setBusy(true);
    try {
      const { url } = await stripeConnect.onboard();
      window.location.href = url; // hosted Stripe onboarding
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not start onboarding');
      setBusy(false);
    }
  }

  const s = status.data;
  const active = s?.charges_enabled;

  return (
    <Card title="Stripe (USD card onramp)">
      <div className="card-body">
        {error && <Alert>{error}</Alert>}
        <p className="muted" style={{ marginTop: 0 }}>
          Optional. USD card onramps work out of the box — no setup needed. Connect your own Stripe
          account only if you want the USD to settle to <em>you</em> directly (instead of the
          platform); USDC still delivers from your float at your USD rate.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span className="muted">Status</span>
          {!s ? (
            <Spinner />
          ) : active ? (
            <Badge tone="success">Active — accepting card payments</Badge>
          ) : s.connected ? (
            <Badge tone="warning">Onboarding incomplete</Badge>
          ) : (
            <Badge tone="neutral">Not connected</Badge>
          )}
        </div>
        {!active && (
          <button className="btn btn-primary" disabled={busy} onClick={connect}>
            {busy ? 'Redirecting…' : s?.connected ? 'Finish Stripe onboarding' : 'Connect Stripe'}
          </button>
        )}
        {active && (
          <button className="btn" disabled={busy} onClick={() => status.reload()}>
            Refresh
          </button>
        )}
      </div>
    </Card>
  );
}

/**
 * The integrator's default conversion prices, NGN per USDC. Orders lock the
 * rate at creation, so edits here never reprice open orders.
 */
export function Rates() {
  const current = useResource(() => rates.get(), []);

  const [onrampRate, setOnrampRate] = useState('');
  const [offrampRate, setOfframpRate] = useState('');
  const [costRate, setCostRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(null);
    setSaved(false);
    setBusy(true);

    try {
      await rates.update({
        ...(onrampRate && { onramp_rate: onrampRate }),
        ...(offrampRate && { offramp_rate: offrampRate }),
        ...(costRate && { cost_rate: costRate }),
      });
      setOnrampRate('');
      setOfframpRate('');
      setCostRate('');
      setSaved(true);
      await current.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save rates');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Rates</h1>
        <p>
          Your conversion prices, in NGN per USDC. You set them; every order locks the rate the
          moment it's created.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}
      {saved && <Alert tone="success">Rates updated.</Alert>}

      {current.loading ? (
        <Spinner />
      ) : (
        <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
          <Card title="Current defaults">
            <div className="card-body">
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 20px', margin: 0 }}>
                <dt className="muted">Onramp (you sell USDC)</dt>
                <dd style={{ margin: 0 }} className="mono">
                  {current.data?.onramp_rate ? `₦${current.data.onramp_rate} / USDC` : 'not set'}
                </dd>
                <dt className="muted">Offramp (you buy USDC)</dt>
                <dd style={{ margin: 0 }} className="mono">
                  {current.data?.offramp_rate ? `₦${current.data.offramp_rate} / USDC` : 'not set'}
                </dd>
                <dt className="muted">Cost basis (your USDC cost)</dt>
                <dd style={{ margin: 0 }} className="mono">
                  {current.data?.cost_rate ? `₦${current.data.cost_rate} / USDC` : 'not set'}
                </dd>
                <dt className="muted">Last updated</dt>
                <dd style={{ margin: 0 }}>{formatDateTime(current.data?.updated_at)}</dd>
              </dl>
            </div>
          </Card>

          <Card title="Update">
            <div className="card-body">
              <label className="field">
                <span>Onramp rate (NGN per USDC)</span>
                <input
                  value={onrampRate}
                  onChange={(event) => setOnrampRate(event.target.value)}
                  placeholder={current.data?.onramp_rate ?? '1650.00'}
                />
              </label>
              <label className="field">
                <span>Offramp rate (NGN per USDC)</span>
                <input
                  value={offrampRate}
                  onChange={(event) => setOfframpRate(event.target.value)}
                  placeholder={current.data?.offramp_rate ?? '1620.00'}
                />
              </label>
              <label className="field">
                <span>Cost basis (NGN per USDC)</span>
                <input
                  value={costRate}
                  onChange={(event) => setCostRate(event.target.value)}
                  placeholder={current.data?.cost_rate ?? '1400.00'}
                />
                <span className="muted" style={{ fontSize: 12.5 }}>
                  What your USDC actually cost you. Used only to compute profit — never applied to
                  an order.
                </span>
              </label>
              <button
                className="btn btn-primary"
                disabled={busy || (!onrampRate && !offrampRate && !costRate)}
                onClick={save}
              >
                {busy ? 'Saving…' : 'Save rates'}
              </button>
            </div>
          </Card>

          <StripeConnectCard />
        </div>
      )}
    </>
  );
}
